import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { MediaUpload } from '@/components/common/MediaUpload';
import { DateTimePicker } from '@/components/common/DateTimePicker';
import { useUpdatePromoOffer } from '../../hooks/usePromoOfferManagement';
import { useMediaUpload } from '@/hooks/useMediaUpload';
import { updatePromoOfferSchema, type UpdatePromoOfferInput } from '../../schemas/promoOfferSchemas';
import type { PromoOffer } from '../../types';

interface EditPromoOfferFormProps {
  promoOffer: PromoOffer;
  onClose: () => void;
  formId?: string;
}

export function EditPromoOfferForm({
  promoOffer,
  onClose,
  formId = 'edit-promo-offer-form',
}: EditPromoOfferFormProps) {
  const [isUploading, setIsUploading] = useState(false);
  const updatePromoOffer = useUpdatePromoOffer();
  const uploadMutation = useMediaUpload({ folder: 'offers' });

  const form = useForm<UpdatePromoOfferInput>({
    resolver: zodResolver(updatePromoOfferSchema),
    defaultValues: {
      promo_code: promoOffer.promo_code,
      brand: promoOffer.brand || '',
      short_description: promoOffer.short_description,
      detailed_description: promoOffer.detailed_description || '',
      media_url: promoOffer.media_url || '',
      expires_at: new Date(promoOffer.expires_at),
      max_claim_per_person: promoOffer.max_claim_per_person,
      max_claim_total: promoOffer.max_claim_total,
      is_active: promoOffer.is_active,
    },
  });

  // Update form when promoOffer changes (e.g., if drawer reopens with different promo offer)
  useEffect(() => {
    form.reset({
      promo_code: promoOffer.promo_code,
      brand: promoOffer.brand || '',
      short_description: promoOffer.short_description,
      detailed_description: promoOffer.detailed_description || '',
      media_url: promoOffer.media_url || '',
      expires_at: new Date(promoOffer.expires_at),
      max_claim_per_person: promoOffer.max_claim_per_person,
      max_claim_total: promoOffer.max_claim_total,
      is_active: promoOffer.is_active,
    });
  }, [promoOffer, form]);

  const handleMediaUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const response = await uploadMutation.mutateAsync(file);
      setIsUploading(false);
      return response;
    } catch (error) {
      setIsUploading(false);
      throw error;
    }
  };

  const onSubmit = async (data: UpdatePromoOfferInput) => {
    try {
      // Only send changed fields
      const changedFields: Partial<UpdatePromoOfferInput> = {};
      let hasChanges = false;

      Object.keys(data).forEach((key) => {
        const fieldKey = key as keyof UpdatePromoOfferInput;
        const newValue = data[fieldKey];
        const oldValue = promoOffer[fieldKey as keyof PromoOffer];

        // Special handling for dates
        if (fieldKey === 'expires_at' && newValue && oldValue) {
          const newDate = new Date(newValue as Date | string).getTime();
          const oldDate = new Date(oldValue as string).getTime();
          if (newDate !== oldDate) {
            changedFields[fieldKey] = newValue instanceof Date ? newValue : new Date(newValue as string);
            hasChanges = true;
          }
        } 
        // Handle optional fields that might be empty strings
        else if (fieldKey === 'brand' || fieldKey === 'detailed_description' || fieldKey === 'media_url') {
          const newVal = (newValue || undefined) as string | undefined;
          const oldVal = (oldValue || undefined) as string | undefined;
          if (newVal !== oldVal) {
            changedFields[fieldKey] = newVal;
            hasChanges = true;
          }
        }
        else if (newValue !== oldValue) {
          changedFields[fieldKey] = newValue as any;
          hasChanges = true;
        }
      });

      if (hasChanges) {
        await updatePromoOffer.mutateAsync({
          id: promoOffer.id,
          data: changedFields as UpdatePromoOfferInput,
        });
      }
      onClose();
    } catch {
      // Error is handled by the mutation
    }
  };

  return (
    <Form {...form}>
      <form
        id={formId}
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-6"
        data-testid="edit-promo-offer-form"
      >
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Venue</h4>
          <p className="text-sm text-muted-foreground">
            {promoOffer.venue_name} (ID: {promoOffer.venue_id})
          </p>
        </div>

        <FormField
          control={form.control}
          name="promo_code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Promo Code</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g., SUMMER2024"
                  className="font-mono"
                  data-testid="edit-promo-offer-form-promo-code"
                />
              </FormControl>
              <FormDescription>
                Unique promo code for this offer (max 50 characters)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="brand"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Brand</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g., Coca-Cola, Nike"
                  data-testid="edit-promo-offer-form-brand"
                />
              </FormControl>
              <FormDescription>
                Sponsoring brand name (optional, max 255 characters)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="short_description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Short Description</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g., Free drink with any purchase"
                  data-testid="edit-promo-offer-form-short-description"
                />
              </FormControl>
              <FormDescription>
                Brief description shown in promo offer lists (max 255 characters)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="detailed_description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Detailed Description</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Enter full promo offer details, terms and conditions..."
                  rows={4}
                  data-testid="edit-promo-offer-form-detailed-description"
                />
              </FormControl>
              <FormDescription>
                Complete promo offer description with all details (optional, max 1000 characters)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="media_url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Promo Offer Image</FormLabel>
              <FormControl>
                <MediaUpload
                  value={field.value}
                  onChange={field.onChange}
                  onUpload={handleMediaUpload}
                  isUploading={isUploading}
                  onError={(error) => form.setError('media_url', { message: error })}
                />
              </FormControl>
              <FormDescription>
                Current image shown above. Upload a new image to replace it (optional, JPEG or PNG, max 5MB)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="expires_at"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Expiry Date & Time</FormLabel>
              <FormControl>
                <DateTimePicker
                  value={field.value}
                  onChange={field.onChange}
                  minDate={new Date()}
                  error={!!form.formState.errors.expires_at}
                />
              </FormControl>
              <FormDescription>
                When this promo offer expires (must be in the future)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="max_claim_per_person"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Max Claims Per Person</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={1}
                    max={100}
                    placeholder="1"
                    data-testid="edit-promo-offer-form-max-claim-per-person"
                    onChange={e => field.onChange(e.target.value ? parseInt(e.target.value) : 1)}
                  />
                </FormControl>
                <FormDescription>
                  Max claims per user (1-100)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="max_claim_total"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Max Total Claims</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={promoOffer.claimed_count || 1}
                    placeholder="100"
                    data-testid="edit-promo-offer-form-max-claim-total"
                    onChange={e => field.onChange(e.target.value ? parseInt(e.target.value) : promoOffer.claimed_count || 1)}
                  />
                </FormControl>
                <FormDescription>
                  Total claims allowed (min: {promoOffer.claimed_count || 1} - current claimed)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {promoOffer.claimed_count > 0 && (
          <div className="rounded-lg bg-muted p-3">
            <p className="text-sm text-muted-foreground">
              <strong>Claims Status:</strong> {promoOffer.claimed_count} / {promoOffer.max_claim_total} claimed
              {promoOffer.active_claims > 0 && ` (${promoOffer.active_claims} active, ${promoOffer.redeemed_claims} redeemed)`}
            </p>
          </div>
        )}

        <FormField
          control={form.control}
          name="is_active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Active</FormLabel>
                <FormDescription>
                  Make this promo offer available to users
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  data-testid="edit-promo-offer-form-is-active"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}